import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { FileText, TrendingUp, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#E31E24', '#22c55e', '#f59e0b', '#3b82f6', '#8b5cf6'];

const LoiAnalysisPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (teamFilter !== 'all') params.append('team', teamFilter);
      if (datePreset !== 'all') params.append('date_preset', datePreset);
      
      const response = await api.get(`/analytics/loi?${params.toString()}`);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load LOI data');
    } finally {
      setLoading(false);
    }
  }, [api, teamFilter, datePreset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pieData = data ? [
    { name: 'Awaiting LOI', value: data.stats.awaitingLoi, color: '#f59e0b' },
    { name: 'Closed LOI', value: data.stats.closedLoi, color: '#22c55e' },
    { name: 'Rejected', value: data.stats.rejectedLoi, color: '#ef4444' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="loi-analysis-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">LOI Analysis</h1>
          <p className="text-muted-foreground">Letter of Intent tracking and conversion insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="w-[140px]" data-testid="team-filter">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              <SelectItem value="Servall">Servall</SelectItem>
              <SelectItem value="R2G">R2G</SelectItem>
              <SelectItem value="Shivaay">Shivaay</SelectItem>
            </SelectContent>
          </Select>
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[140px]" data-testid="date-filter">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : data && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="total-leads-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                    <p className="text-3xl font-bold">{data.stats.totalLeads}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="awaiting-loi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Awaiting LOI</p>
                    <p className="text-3xl font-bold text-amber-600">{data.stats.awaitingLoi}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="closed-loi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Closed LOI</p>
                    <p className="text-3xl font-bold text-green-600">{data.stats.closedLoi}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="conversion-rate-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    <p className="text-3xl font-bold text-primary">{data.stats.conversionRate}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LOI Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>LOI Distribution</CardTitle>
                <CardDescription>Breakdown of LOI status</CardDescription>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No LOI data available</p>
                )}
              </CardContent>
            </Card>

            {/* LOI by City Chart */}
            <Card>
              <CardHeader>
                <CardTitle>LOI by City</CardTitle>
                <CardDescription>City-wise LOI distribution</CardDescription>
              </CardHeader>
              <CardContent>
                {data.byCity && data.byCity.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.byCity} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="city" width={80} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="awaiting" name="Awaiting" fill="#f59e0b" stackId="a" />
                        <Bar dataKey="closed" name="Closed" fill="#22c55e" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No city data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Executive Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Executive LOI Performance</CardTitle>
              <CardDescription>LOI conversion by executive</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Executive</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead className="text-center">Awaiting LOI</TableHead>
                    <TableHead className="text-center">Closed LOI</TableHead>
                    <TableHead className="text-center">Conversion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.byExecutive && data.byExecutive.length > 0 ? (
                    data.byExecutive.map((exec) => (
                      <TableRow key={exec.executiveId}>
                        <TableCell className="font-medium">{exec.executiveName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{exec.team}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            {exec.awaitingLoi}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-green-100 text-green-800">
                            {exec.closedLoi}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {exec.conversionRate}%
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No executive data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Recent LOI Leads */}
          <Card>
            <CardHeader>
              <CardTitle>Recent LOI Activity</CardTitle>
              <CardDescription>Latest leads in LOI pipeline</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.recentLeads && data.recentLeads.length > 0 ? (
                  data.recentLeads.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{lead.name}</p>
                        <p className="text-sm text-muted-foreground">{lead.city} • {lead.mobile}</p>
                      </div>
                      <Badge className={lead.status === 'Closed LOI' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {lead.status}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-center py-4 text-muted-foreground">No recent LOI activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LoiAnalysisPage;
